import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { DEMO_DEALS } from "../../data/portalDeals.js";
import { commitmentTypeLabel, routeBlurb } from "../../data/portalDemo.js";

const ROUTE_FILTERS = [
  { key: "all",                  label: "All routes" },
  { key: "uk_warehouse_fl",      label: "UK Warehouse · Pool B-Note" },
  { key: "uk_whole_loan_bnote",  label: "UK Whole-Loan · Single-deal B-Note" },
  { key: "hospitality",          label: "Hospitality" },
  { key: "india_lux_feeder",     label: "India · Lux Feeder" },
  { key: "india_ncd_direct",     label: "India · NCD direct" }
];

export default function PortalPipeline() {
  const [routeFilter, setRouteFilter] = useState("all");

  const filtered = useMemo(() => {
    const live = DEMO_DEALS.filter(d => d.status === "live");
    if (routeFilter === "all") return live;
    return live.filter(d => d.route === routeFilter);
  }, [routeFilter]);

  // Group filtered deals by route for "All" view
  const groups = useMemo(() => {
    const m = new Map();
    for (const d of filtered) {
      if (!m.has(d.route)) m.set(d.route, []);
      m.get(d.route).push(d);
    }
    return m;
  }, [filtered]);

  function fmtCcy(amount, ccy) {
    if (amount == null) return "—";
    const sym = ccy === "GBP" ? "£" : ccy === "USD" ? "$" : ccy === "EUR" ? "€" : ccy === "INR" ? "₹" : "";
    if (ccy === "INR" && amount >= 10_000_000) return `${sym}${(amount / 10_000_000).toFixed(1)}Cr`;
    if (amount >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(1)}m`;
    if (amount >= 1_000)     return `${sym}${(amount / 1_000).toFixed(0)}k`;
    return `${sym}${amount}`;
  }

  return (
    <div className="portal-page">
      <h1 className="portal-h1">Live deal pipeline</h1>
      <p className="muted">
        Daksfirst-originated investment opportunities across five routes. Click a deal
        to view its teaser; the full memo, security pack and data room sit behind a
        per-deal CA/NDA.
      </p>

      <div className="route-filter-bar">
        {ROUTE_FILTERS.map(r => (
          <button key={r.key}
                  className={"route-filter" + (routeFilter === r.key ? " active" : "")}
                  onClick={() => setRouteFilter(r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      {Array.from(groups.entries()).map(([route, deals]) => (
        <section key={route} className="route-group">
          <div className="route-group-head">
            <h2 className="portal-h2">{commitmentTypeLabel(route)}</h2>
            <p className="muted small">{routeBlurb(route)}</p>
          </div>
          <div className="deal-grid">
            {deals.map(d => (
              <Link key={d.slug} to={`/portal/pipeline/${d.slug}`} className="deal-card">
                <div className="deal-card-hero">
                  <span className="deal-card-sector">{d.sector}</span>
                </div>
                <div className="deal-card-body">
                  <h3 className="deal-card-name">{d.name}</h3>
                  <div className="deal-card-loc muted">{d.location}</div>
                  <p className="deal-card-line">{d.oneLiner}</p>
                  <div className="deal-card-stats">
                    <div><span className="muted">Target net IRR</span><strong>{d.targetNetIRR.toFixed(1)}%</strong></div>
                    <div><span className="muted">Ticket</span><strong>{d.ticketSize}</strong></div>
                    <div><span className="muted">Total</span><strong>{fmtCcy(d.totalSize, d.currency)}</strong></div>
                    <div><span className="muted">LTV</span><strong>{d.ltv}</strong></div>
                    <div><span className="muted">Term</span><strong>{d.termMonths}m</strong></div>
                    <div><span className="muted">Closes</span><strong>{d.fundraiseClose}</strong></div>
                  </div>
                </div>
                <div className="deal-card-cta">
                  <span className="pill pill-warn">CA/NDA required</span>
                  <span className="deal-card-arrow">View deal →</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {!filtered.length && (
        <p className="muted">No live deals match this filter right now.</p>
      )}
    </div>
  );
}
